import { Map, List } from "immutable";
import snakeCase from "lodash/snakeCase";
import { plural } from "pluralize";
import { createSelector } from "reselect";
import { getLoggedUserId } from "./selector.users.jsx";
import i18n from "../../i18n";

const resolveParentType = (type) => {
  switch (type) {
    case "UserMessage": {
      return "ConversationMessage";
    }
    default: {
      return type;
    }
  }
};

export const getAllReactions = (state) => state.reactions.collection;
export const getAllReactionTypes = (state) => state.reactions.types;
export const getAllMyWorkReactions = (state) => state.reactions.myWorkCollection;

const getMyWorkReactionsByObjectId = createSelector(
  getAllMyWorkReactions,
  (state, id, type) => type,
  (state, id) => id,
  (collection, type, id) => collection.get(`${type}`, Map()).get(`${id}`, List())
);

export const makeMyWorkReactionsByObjectSelector = () => {
  return createSelector(getMyWorkReactionsByObjectId, getAllReactionTypes, (myWorkReactions, reactionTypes) => {
    let reactions = myWorkReactions.map((reaction, type) => {
      return {
        type,
        shortName: reactionTypes.get(type).get(`short_name`),
        count: reaction.size,
      };
    });

    return reactions.toArray();
  });
};

const defaultReactionsList = List();
const defaultParent = Map({ reactions: defaultReactionsList });

const getParentById = (state, parentType = "", id = 0) => {
  const type = snakeCase(plural(parentType));
  if (!state[type]) {
    throw new Error(`Parent type is not valid!`);
  }
  return state[type].get(`${id}`, defaultParent);
};

const getReactionIdsByParent = (state, type, id) => {
  return getParentById(state, resolveParentType(type), id).get(`reactions`, defaultReactionsList);
};

const makeGetReactionTypes = () => {
  return createSelector(getReactionIdsByParent, getAllReactions, (ids, reactions) =>
    ids.map((id) => {
      let reaction = reactions.get(`${id}`);
      if (reaction) {
        return reaction.get(`class`);
      }
      return undefined;
    })
  );
};

export const makeGroupedReactionsByParentSelector = () => {
  const getReactionTypes = makeGetReactionTypes();
  return createSelector(getReactionTypes, (types) => {
    return types.reduce((a, b) => {
      if (b && a.indexOf(b) === -1) {
        a.push(b);
      }
      return a;
    }, []);
  });
};

export const makeReactionCountByParentSelector = (reactionType) => {
  const getReactionTypes = makeGetReactionTypes();
  return createSelector(getReactionTypes, getAllReactionTypes, (types, availableTypes) => {
    return types
      .toArray()
      .map((type) => {
        return availableTypes.find((availableType) => availableType.get(`type`) === type).get(`short_name`);
      })
      .filter((type) => type === reactionType).length;
  });
};

const defaultAvailableReaction = Map();

const getAvailableReactionByType = (state, type) => {
  return getAllReactionTypes(state).get(`${type}`, defaultAvailableReaction);
};

const makeGetAllReactionsByParent = () =>
  createSelector(getReactionIdsByParent, getAllReactions, (ids, reactions) => {
    if (!List.isList(ids)) {
      ids = List(ids);
    }

    return ids.map((id) => reactions.get(`${id}`)).filter((id) => !!id);
  });

export const makeReactionPropsByParentSelector = () => {
  const getAllReactionsByParent = makeGetAllReactionsByParent();
  return createSelector(
    getAllReactionsByParent,
    getLoggedUserId,
    (state, parentType, parentId, type) => getAvailableReactionByType(state, type),
    (reactions, logged_user_id, availableReaction) => {
      const groupedReactionsByType = reactions.groupBy((reaction) => reaction.get(`class`));
      const typeReactions = groupedReactionsByType
        .get(availableReaction.get(`type`), List())
        .groupBy(
          (reaction) => `${reaction.get("parent_type")}-${reaction.get("parent_id")}-${reaction.get("created_by_id")}`
        )
        .map((reaction) => reaction.first())
        .toList()
        .sort((reaction1, reaction2) => {
          if (reaction1.get(`created_by_id`) === logged_user_id) return -1;
          if (reaction2.get(`created_by_id`) === logged_user_id) return 1;
          return 0;
        });

      const myReaction = typeReactions.find((reaction) => reaction.get(`created_by_id`) === logged_user_id);

      const creators = typeReactions
        .take(5)
        .map((reaction) =>
          reaction.get(`created_by_id`) === logged_user_id ? i18n.t("You") : reaction.get(`created_by_name`)
        );

      let content = creators.toArray().join(`, `);
      if (typeReactions.size > 5) {
        content = `${content} + ${typeReactions.size - 5}`;
      }
      let reacted = false;
      let pending = false;

      if (myReaction) {
        pending = myReaction.get(`pending`, false);
        reacted = true;
      }

      let values = [];
      let uniqueReactions = reactions.filter((reaction) => {
        let value;

        if (reaction.get(`created_by_id`) !== 0) {
          value = `${reaction.get(`class`)}|${reaction.get(`created_by_id`)}|${reaction.get(`parent_type`)}`;
        } else {
          value = `${reaction.get(`class`)}|${reaction.get(`created_by_email`)}|${reaction.get(`parent_type`)}`;
        }

        if (!values.includes(value)) {
          values.push(value);
          return true;
        }
      });

      return {
        count: uniqueReactions.countBy((reaction) => reaction.get(`class`)).get(availableReaction.get(`type`)) || 0,
        shortName: availableReaction.get(`short_name`),
        content,
        reacted,
        pending,
      };
    }
  );
};

export const makeIsMyReactionByTypeSelector = () => {
  let getAllReactionsByParent = makeGetAllReactionsByParent();
  return createSelector(
    getAllReactionsByParent,
    getLoggedUserId,
    (state, parentType, parentId, type) => type,
    (reactions, logged_user_id, type) => {
      let filtered_reactions = reactions.filter((reaction) => {
        return reaction.get(`class`) === type;
      });

      let myReaction = filtered_reactions.find((reaction) => {
        return reaction.get(`created_by_id`) === logged_user_id;
      });

      return myReaction !== undefined;
    }
  );
};

export const getAvailableReactionTypes = createSelector(getAllReactionTypes, (types) => {
  return types.toArray().map((availableReaction) => {
    return availableReaction.get(`type`);
  });
});

export const makeGetReactionIdAddedByLoggedUserId = (reactionType) => {
  let getAllReactionsByParent = makeGetAllReactionsByParent();
  return createSelector(getLoggedUserId, getAllReactionsByParent, (userId, parentReactions) => {
    let id = 0;
    parentReactions.map((parentReaction) => {
      if (parentReaction.get(`created_by_id`) === userId && parentReaction.get(`class`) === reactionType) {
        return (id = parentReaction.get(`id`));
      }
    });

    return id;
  });
};

export const getAvailableReactionShortNameByType = (state, type) => {
  let item = getAllReactionTypes(state).get(`${type}`);
  if (item) {
    return item.get(`short_name`);
  }
  return undefined;
};

export const makeParentHasReaction = () => {
  return createSelector(getReactionIdsByParent, (reactionIds) => reactionIds.size > 0);
};

export const getReactionTypes = createSelector(getAllReactionTypes, (types) => {
  return types
    .toList()
    .toJS()
    .reduce((prev, next) => {
      prev[next.type] = next.short_name;
      return prev;
    }, {});
});
